/**
 * GettingStartedModal — the "How to use RV Match AI" walkthrough that opens
 * when a dealer clicks the "?" icon in the top nav (or "Open guide" on the
 * onboarding checklist). It's a scrollable, sectioned modal that explains
 * every tab of the dashboard in plain English.
 *
 * Deliberately static — content is baked into the component so it opens
 * instantly and works offline. Update this file when the dashboard's layout
 * changes so the guide never drifts from reality.
 */
import React, { useEffect } from 'react';
import {
  X, Sparkles, Kanban, Mail, Send, Contact, MessagesSquare, Megaphone,
  BarChart3, ShoppingBag, Users, Settings, ExternalLink, MessageSquare, Bot,
  Phone,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'overview',
    icon: Sparkles,
    title: 'The Overview tab',
    body: (
      <>
        <p>
          Your home base. The AI Briefing at the top summarises today’s most
          important leads. Below that, Quick Stats show total leads, active
          conversations, average intent score, and how many emails you sent.
        </p>
        <p>
          Use the Command Bar (⌘/Ctrl-K or the search box) to jump to any lead
          or tab without clicking through the menu.
        </p>
      </>
    ),
  },
  {
    id: 'pipeline',
    icon: Kanban,
    title: 'The Pipeline (Kanban)',
    body: (
      <>
        <p>
          Every lead appears in the <strong>New</strong> column when they
          first submit the form or talk to Ranger. Drag them right as you
          work them: <em>Contacted → Qualified → Won</em>.
        </p>
        <p>
          Click any card to open the full lead profile — form answers, tow
          vehicle, budget, notes, and the whole email thread all live there.
        </p>
      </>
    ),
  },
  {
    id: 'email',
    icon: Mail,
    title: 'Email Center',
    body: (
      <>
        <p>
          All buyer replies land in <strong>Inbox</strong>. Every email you
          send from a lead card is filed under <strong>Sent</strong>. Templates
          + your Signature save time on repeat outreach.
        </p>
        <p>
          Replies from buyers auto-thread onto their lead — you never have to
          copy anything between apps.
        </p>
      </>
    ),
  },
  {
    id: 'ranger',
    icon: Bot,
    title: 'Ranger AI Chat',
    body: (
      <>
        <p>
          Ranger is the AI chat widget on your public link. Buyers can ask
          questions and get personalised RV recommendations 24/7. Every
          conversation becomes a lead automatically attributed to you.
        </p>
        <p>
          You can also open Ranger from your dashboard to test how a buyer
          sees it.
        </p>
      </>
    ),
  },
  {
    id: 'appointments',
    icon: Phone,
    title: 'Appointments',
    body: (
      <>
        <p>
          The <strong>Appointments</strong> tab is where every call and
          visit request from your leads lands — from Ranger bookings, the
          &ldquo;Book a Call&rdquo; button on your public link, and
          dashboard-initiated scheduling. Each card shows the buyer&rsquo;s
          name, email, phone, and the day + time slot they picked, so you
          can confirm, reschedule, or mark it complete without hunting
          through email.
        </p>
        <p>
          <strong>VIP calls jump the queue.</strong> Any booking tied to a
          VIP Buying Plan or VIP Concierge purchase carries a fuchsia
          <em> VIP badge</em>, sorts to the very top of the list regardless
          of filter, and shows how many included calls the buyer has left
          in their VIP window. Concierge bookings sort above VIP Plan
          bookings above everything else.
        </p>
        <p>
          Filter tabs across the top let you jump between{' '}
          <strong>All / VIP / Pending / Confirmed / Completed / Cancelled</strong>{' '}
          — the Pending badge shows an unread count so you never miss a
          fresh request.
        </p>
      </>
    ),
  },
  {
    id: 'marketing',
    icon: Megaphone,
    title: 'Marketing Center',
    body: (
      <>
        <p>
          Your <strong>Public Link</strong> lives here. Click <em>Change</em>{' '}
          next to the URL to customize your slug (e.g.{' '}
          <code>/bay-city-rv</code>). Old links keep working forever — they
          auto-redirect.
        </p>
        <p>
          Also inside: ready-made Facebook / SMS / email templates you can
          copy-paste, plus a Business Card uploader for polished intros.
        </p>
      </>
    ),
  },
  {
    id: 'crm',
    icon: Contact,
    title: 'CRM Hub & Contacts',
    body: (
      <>
        <p>
          Connect Google Contacts once and every new lead can be pushed to
          your phone with a single click from the lead card.
        </p>
        <p>
          The vCard button downloads a .vcf file if you use anything other
          than Google.
        </p>
      </>
    ),
  },
  {
    id: 'support',
    icon: MessagesSquare,
    title: 'Advisor Support Chat',
    body: (
      <>
        <p>
          Stuck? Have feedback? Open the Advisor Support tab and message the RV
          Match AI team directly. Replies typically arrive within a business
          day.
        </p>
      </>
    ),
  },
  {
    id: 'sales',
    icon: BarChart3,
    title: 'Sales Tracker & Analytics',
    body: (
      <>
        <p>
          Log every RV you sell to see revenue trends, top RV types, and
          conversion rates over time. Analytics on the Overview tab pull from
          this data.
        </p>
      </>
    ),
  },
  {
    id: 'inventory',
    icon: ShoppingBag,
    title: 'Inventory',
    body: (
      <>
        <p>
          Optional. Paste in your VINs and the AI matcher will surface your
          own units first in a buyer’s match list. Without inventory, buyers
          still get matched to industry-standard floor plans and you get the
          lead.
        </p>
      </>
    ),
  },
  {
    id: 'admin',
    icon: Users,
    title: 'Team & Settings',
    body: (
      <>
        <p>
          Invite team members, change your password (Settings → Change
          Password), and connect your Google account to push contacts.
        </p>
        <p>
          Your subscription and billing questions are handled by RV Match AI
          support — reach us via Advisor Support Chat.
        </p>
      </>
    ),
  },
  {
    id: 'money',
    icon: ExternalLink,
    title: 'How you earn (and how we earn)',
    body: (
      <>
        <p>
          <strong>Your job is to collect leads.</strong> Every buyer who
          completes the form on <em>your</em> public link, or chats with
          Ranger while on your link, is <em>yours</em> — attributed only to
          your dashboard.
        </p>
        <p>
          Membership sales, add-on tools, and any e-commerce on
          rvmatchai.app belong to the platform. Dealers focus purely on lead
          conversion. Your ROI comes from the RVs you close, not from
          re-selling our products.
        </p>
      </>
    ),
  },
];

const GettingStartedModal = ({ open, onClose, isDark = true, onGoTo }) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const goTo = (tab) => {
    onGoTo?.(tab);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[105] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="getting-started-title"
      data-testid="getting-started-modal"
    >
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={onClose}
        data-testid="getting-started-backdrop"
      />
      <div
        className={`relative w-full sm:max-w-3xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[100vh] sm:max-h-[90vh] ${
          isDark ? 'bg-[#0b1220] text-white border border-white/10' : 'bg-white text-gray-900 border border-gray-200'
        }`}
      >
        {/* Sticky header */}
        <div className={`sticky top-0 z-10 px-5 sm:px-8 py-4 sm:py-5 flex items-start gap-3 border-b backdrop-blur ${
          isDark ? 'bg-[#0b1220]/95 border-white/10' : 'bg-white/95 border-gray-200'
        }`}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
            isDark ? 'bg-amber-400/15 text-amber-300' : 'bg-amber-100 text-amber-700'
          }`}>
            <Sparkles size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="getting-started-title"
              className="text-lg sm:text-2xl font-bold leading-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              How to use your RV Match AI dashboard
            </h2>
            <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Every tab explained in plain English. Jump to any section, or scroll through.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`shrink-0 -mr-1 p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            }`}
            aria-label="Close"
            data-testid="getting-started-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Two-pane layout on desktop, single scroll on mobile */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Left rail — jump nav (desktop only) */}
          <nav
            className={`hidden md:block w-52 shrink-0 overflow-y-auto py-4 pl-6 pr-2 border-r ${
              isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <ul className="space-y-1">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#gs-${s.id}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isDark ? 'text-slate-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(`gs-${s.id}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    data-testid={`gs-nav-${s.id}`}
                  >
                    <s.icon size={14} />
                    <span className="truncate">{s.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right content — scrollable sections */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-6 space-y-6">
            {SECTIONS.map((s) => (
              <section
                key={s.id}
                id={`gs-${s.id}`}
                className={`rounded-xl border p-4 sm:p-5 ${
                  isDark ? 'border-white/10 bg-white/[0.03]' : 'border-gray-200 bg-gray-50'
                }`}
                data-testid={`gs-section-${s.id}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    isDark ? 'bg-amber-400/15 text-amber-300' : 'bg-amber-100 text-amber-700'
                  }`}>
                    <s.icon size={16} />
                  </div>
                  <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {s.title}
                  </h3>
                </div>
                <div className={`text-sm space-y-2 ${isDark ? 'text-white/85' : 'text-gray-700'}`}>
                  {s.body}
                </div>
              </section>
            ))}

            {/* Bottom help card */}
            <div
              className={`rounded-xl p-4 sm:p-5 flex items-start gap-3 ${
                isDark ? 'bg-amber-400/10 border border-amber-400/25' : 'bg-amber-50 border border-amber-200'
              }`}
            >
              <MessageSquare size={20} className={isDark ? 'text-amber-300' : 'text-amber-700'} />
              <div className="flex-1 min-w-0">
                <div className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Still stuck?
                </div>
                <p className={`text-sm mt-0.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  Open Advisor Support Chat and message the RV Match AI team. We
                  usually reply within one business day.
                </p>
              </div>
              <button
                type="button"
                onClick={() => goTo('support')}
                className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                  isDark ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
                data-testid="gs-open-support"
              >
                Open Support
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`px-5 sm:px-8 py-3 sm:py-4 border-t flex justify-end ${
            isDark ? 'border-white/10 bg-[#0b1220]' : 'border-gray-200 bg-white'
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-5 py-2 rounded-lg font-bold text-sm transition-colors ${
              isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-900 hover:bg-black text-white'
            }`}
            data-testid="getting-started-done"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedModal;
